import { useEffect, useMemo, useState } from 'react';

import { Box, Button, Divider, FormLabel, Typography } from '@mui/material';
import { Hierarchy } from '@prisma/client';

import CountrySelectInput from 'src/components/CountrySelectInput/CountrySelectInput';
import { DropDown } from 'src/components/FormikComponents';
import { OptionTypes } from 'src/components/FormikComponents/DropDown';
import MainCard from 'src/components/MainCard/MainCard';
import { showErrorToast } from 'src/components/Toasts/toast';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import { useAuthContext } from 'src/contexts/AuthProvider';
import { isIreland } from 'src/lib/countryConfig';
import { isApplicationEditable } from 'src/pages/MerchantApplicationPage/merchantApplicationHelpers.util';
import { supportedCountries } from 'src/utils/utils';

export const SelectPartner = ({ formikProps, data }) => {
    const { user } = useAuthContext();

    const [partnerData, setPartnerData] = useState(null);
    const [partnerOfPartnerData, setPartnerOfPartnerData] = useState(null);
    const { merchantApplication } = useAddMerchantContext();
    const isEditingDisabled = isApplicationEditable(merchantApplication);

    useEffect(() => {
        if (user?.role?.hierarchyLevel === Hierarchy.PARTNER_OF_PARTNER) {
            setPartnerData(user?.partner?.business?.legalName);
            setPartnerOfPartnerData(user?.business?.legalName);
            // Set form values directly:
            formikProps.setFieldValue('partner', user?.partner?.id.toString());
            formikProps.setFieldValue(
                'partnerOfPartner',
                user?.business?.partnerOfPartnerId.toString()
            );
        }
        if (user?.role?.hierarchyLevel === Hierarchy.PARTNER) {
            setPartnerData(user?.business?.legalName);
            // Set form values directly:
            formikProps.setFieldValue('partner', user?.partner?.id.toString());
        }
    }, [user, formikProps.setFieldValue]);

    const partnerOfPartners = useMemo(() => {
        if (formikProps.values.partner) {
            // find partner of partners for the selected partner
            const partner = data?.activePartners.find((partner) => {
                return partner?.id.toString() === formikProps.values.partner;
            });
            return partner?.partnerOfPartner || [];
        }
        return [];
    }, [data?.activePartners, formikProps.values.partner]);

    const partnerOptions: OptionTypes[] = useMemo(
        () => {
            return data?.activePartners
                ?.map((activePartners) => {
                    const label = activePartners?.business?.legalName?.replace(/_/g, ' ');

                    if (!label) {
                        return null; // If the label is empty, return null to skip rendering it.
                    }

                    return {
                        value: activePartners?.id.toString() || '',
                        label,
                    };
                })
                .filter((activePartners) => {
                    return activePartners;
                });
        }, // Use filter to remove any null values from the array.
        [data]
    );

    const partnerOfPartnerOptions: OptionTypes[] = useMemo(
        () => {
            return partnerOfPartners
                ?.map((partnerOfPartner) => {
                    const label = partnerOfPartner?.business?.legalName?.replace(/_/g, ' ');

                    if (!label) {
                        return null; // If the label is empty, return null to skip rendering it.
                    }

                    return {
                        value: partnerOfPartner?.id.toString() || '',
                        label,
                    };
                })
                .filter(Boolean);
        }, // Use filter to remove any null values from the array.
        [partnerOfPartners]
    );

    return (
        <div>
            <MainCard>
                <Typography fontSize="24px" color="#575757" marginBottom="8px">
                    Select Partner
                </Typography>
                <Typography fontSize="14px" color="#575757" marginBottom="8px">
                    Add to a partner or a partner of a partner
                </Typography>
                <Divider
                    sx={{
                        borderColor: 'warning.main',
                        marginBottom: '34px',
                    }}
                />
                <Box display="flex" flexDirection="row" width="100%" gap={2}>
                    {/* Left Column: Country */}
                    <Box flex={0.49} display="flex" flexDirection="column">
                        <CountrySelectInput
                            setFieldValue={(__, value) => {
                                formikProps.setFieldValue('country', value);
                            }}
                            filterByAlpha2Codes={supportedCountries(isIreland)}
                            fieldName="country"
                            label="Select Country"
                            placeholder="Select Country"
                        />
                    </Box>

                    <Box flex={0.49} display="flex" flexDirection="column" gap={2}>
                        {user?.role?.hierarchyLevel === Hierarchy.NPI &&
                        formikProps.values.country ? (
                            <DropDown
                                name="partner"
                                label="Select a Partner"
                                placeholder="Select Partner"
                                options={partnerOptions}
                                disabled={isEditingDisabled}
                            />
                        ) : (
                            <Box display="flex" flexDirection="column" gap={0.5}>
                                <FormLabel sx={{ color: 'grey.900', fontWeight: 600 }}>
                                    Partner
                                </FormLabel>
                                <input
                                    value={partnerData}
                                    style={{
                                        minWidth: '192px',
                                        height: '52px',
                                        borderRadius: '8px',
                                        border: '1px solid #B4B4B3',
                                        padding: '12px',
                                    }}
                                    disabled
                                />
                            </Box>
                        )}

                        {partnerOfPartners.length > 0 ? (
                            <DropDown
                                name="partnerOfPartner"
                                label="Select Partner of Partner"
                                options={partnerOfPartnerOptions}
                                disabled={isEditingDisabled}
                            />
                        ) : (
                            <>
                                <FormLabel sx={{ color: 'grey.900', fontWeight: 600 }}>
                                    Partner of Partner
                                </FormLabel>
                                <input
                                    value={partnerOfPartnerData}
                                    style={{
                                        minWidth: '192px',
                                        height: '52px',
                                        borderRadius: '8px',
                                        border: '1px solid #B4B4B3',
                                        padding: '12px',
                                    }}
                                    disabled
                                />
                            </>
                        )}
                    </Box>
                </Box>
            </MainCard>
            <Box display="flex" justifyContent="flex-end">
                <Button
                    sx={{
                        alignSelf: 'end',
                        marginTop: '24px',
                    }}
                    type="submit"
                    variant="main"
                    onClick={async () => {
                        try {
                            await formikProps.submitForm();
                        } catch (error) {
                            showErrorToast('There is an error with retrieving your Partner');
                        }
                    }}
                >
                    Next
                </Button>
            </Box>
        </div>
    );
};

export default SelectPartner;
